import { useMemo, useEffect, useState } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { Nav } from './components/Nav'
import { ChannelList } from './pages/ChannelList'
import { ChannelDetail } from './pages/ChannelDetail'
import { Onboarding } from './pages/Onboarding'
import { Success } from './pages/Success'
import { NoAccess } from './pages/NoAccess'
import { AdminDeleteChannels } from './pages/AdminDeleteChannels'
import { useSession } from './hooks/useSession'
import { useChannels } from './hooks/useChannels'
import { getChannel } from './api/client'

function useQueryParam(name: string): string | null {
  return useMemo(
    () => new URLSearchParams(window.location.search).get(name),
    [],
  )
}

export default function App() {
  const magicToken = useQueryParam('token')
  const { session, status, error, logout } = useSession(magicToken)

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: 'var(--muted)', fontSize: 15 }}>
        Loading…
      </div>
    )
  }

  if (status === 'unauthenticated' || status === 'error') {
    return <NoAccess error={error} />
  }

  return <AuthenticatedApp session={session!} onLogout={logout} freshFromToken={!!magicToken} />
}

function AuthenticatedApp({
  session,
  onLogout,
  freshFromToken,
}: {
  session: NonNullable<ReturnType<typeof useSession>['session']>
  onLogout: () => void
  freshFromToken: boolean
}) {
  const navigate = useNavigate()
  const { channels, loading, error } = useChannels(session.sessionToken)
  const [channelRedirectError, setChannelRedirectError] = useState<string | null>(null)

  const userName = session.userName ?? session.userId

  useEffect(() => {
    if (!freshFromToken || !session.channelId) return

    getChannel(session.sessionToken, session.channelId)
      .then(channel => {
        if (channel.isConfigured) {
          navigate(`/channels/${session.channelId}`, { replace: true })
        } else {
          navigate(`/onboarding`, { replace: true })
        }
      })
      .catch((err: { status?: number }) => {
        if (err?.status === 403) {
          setChannelRedirectError("You need to be a member of this channel to configure it.")
        } else {
          navigate(`/channels/${session.channelId}`, { replace: true })
        }
      })
  }, []) // only run once on mount

  if (channelRedirectError) {
    return <NoAccess error={channelRedirectError} />
  }

  return (
    <>
      <Nav userName={userName} isAdmin={session.isAdmin} onLogout={onLogout} />
      <Routes>
        <Route
          path="/"
          element={<ChannelList channels={channels} loading={loading} error={error} />}
        />
        <Route
          path="/channels/:channelId"
          element={<ChannelDetail sessionToken={session.sessionToken} isAdmin={session.isAdmin} />}
        />
        <Route
          path="/onboarding"
          element={
            <Onboarding
              channels={channels}
              sessionToken={session.sessionToken}
              preselectedChannelId={session.channelId}
            />
          }
        />
        <Route path="/success" element={<Success />} />
        {session.isAdmin && (
          <Route
            path="/admin/channels"
            element={<AdminDeleteChannels channels={channels} sessionToken={session.sessionToken} />}
          />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
